import { alpha, type Theme } from "@mui/material/styles";
import type { AppSx } from "../types";
import { buildFormLayoutStyles } from "../layout/formLayout";

export interface WidgetEditorComponentStyles {
  visualAlert: AppSx;
  visualTableHeader: AppSx;
  visualProbeLoading: AppSx;
  visualResultGrid: AppSx;
  visualAddColumnRow: AppSx;
  draftApprovalRoot: AppSx;
  draftApprovalAlert: AppSx;
  draftApprovalSection: AppSx;
  draftApprovalSummary: AppSx;
  draftApprovalTitle: AppSx;
  draftApprovalDetails: AppSx;
  draftApprovalDetailsHidden: AppSx;
  draftOutputTitle: AppSx;
  draftOutputEmbeddedLabel: AppSx;
  draftOutputChartHeader: AppSx;
  draftOutputEmbedded: AppSx;
  draftOutputWarningPanel: AppSx;
  draftSummaryRow: AppSx;
  draftSummaryLabel: AppSx;
  draftSummaryValue: AppSx;
  draftSummaryRoot: AppSx;
  draftSummaryChipsRow: AppSx;
  draftSummaryChip: AppSx;
  parametersStack: AppSx;
  parametersAddButton: (compact: boolean) => AppSx;
  parametersEmpty: (compact: boolean) => AppSx;
  parametersCardGrid: (compact: boolean) => AppSx;
  parametersDeleteButton: (compact: boolean) => AppSx;
  dataSourcesIndexLabel: AppSx;
  dataSourcesGrid: AppSx;
  sourceDateRangesStack: AppSx;
  sourceDateRangesGrid: AppSx;
  versionHistoryHeader: AppSx;
  versionHistorySubtitle: AppSx;
  versionHistoryError: AppSx;
  versionHistoryLoading: AppSx;
  versionHistoryRow: AppSx;
}

export function buildWidgetEditorComponentStyles(theme: Theme): WidgetEditorComponentStyles {
  const form = buildFormLayoutStyles(theme);
  return {
    visualAlert: { py: 0.5 },
    visualTableHeader: { mb: 1 },
    visualProbeLoading: { mb: 1 },
    visualResultGrid: { mb: 1.5 },
    visualAddColumnRow: { mt: 1 },
    draftApprovalRoot: {
      width: "100%",
      maxWidth: "100%",
      overflow: "hidden",
      boxSizing: "border-box",
      "& .MuiInputBase-root": { bgcolor: "background.paper" },
    },
    draftApprovalAlert: { mb: 1, py: 0.25, fontSize: "0.8rem" },
    draftApprovalSection: {
      "&:before": { display: "none" },
      boxShadow: "none",
      bgcolor: "transparent",
      border: 1,
      borderColor: "divider",
      borderRadius: 1,
      mb: 0.75,
      "&.Mui-expanded": { mb: 0.75 },
    },
    draftApprovalSummary: { minHeight: 36, py: 0 },
    draftApprovalTitle: { fontWeight: 700 },
    draftApprovalDetails: { pt: 0, px: 1, pb: 1 },
    draftApprovalDetailsHidden: { pt: 0, px: 0.5, pb: 1, overflow: "hidden" },
    draftOutputTitle: { fontWeight: 600 },
    draftOutputEmbeddedLabel: { fontWeight: 600, display: "block" },
    draftOutputChartHeader: { mb: 1 },
    draftOutputEmbedded: { pt: 0.5 },
    draftOutputWarningPanel: {
      p: 1.5,
      borderRadius: 1,
      border: 1,
      borderColor: "warning.main",
      bgcolor:
        theme.palette.mode === "dark"
          ? "rgba(255, 167, 38, 0.08)"
          : "rgba(255, 167, 38, 0.12)",
      "& .MuiInputBase-root": { bgcolor: "background.paper" },
    },
    draftSummaryRow: {
      display: "grid",
      gridTemplateColumns: { xs: "88px 1fr", sm: "100px 1fr" },
      gap: 0.5,
      alignItems: "baseline",
      py: 0.25,
    },
    draftSummaryLabel: { fontWeight: 600 },
    draftSummaryValue: { wordBreak: "break-word" },
    draftSummaryRoot: {
      px: 1,
      py: 0.75,
      borderRadius: 1,
      border: 1,
      borderColor: "divider",
      bgcolor: "background.paper",
    },
    draftSummaryChipsRow: { display: "flex", flexWrap: "wrap", gap: 0.5, pt: 0.5 },
    draftSummaryChip: { height: 22, fontSize: "0.7rem" },
    parametersStack: { width: "100%", maxWidth: "100%" },
    parametersAddButton: (compact) => ({
      ...form.formRowButton,
      textTransform: "none",
      fontWeight: 600,
      fontSize: compact ? "0.75rem" : undefined,
    }),
    parametersEmpty: (compact) => ({
      fontSize: compact ? "0.8rem" : undefined,
    }),
    parametersCardGrid: (compact) => ({ p: compact ? 1 : 1.5 }),
    parametersDeleteButton: (compact) => ({
      alignSelf: compact ? "center" : { lg: "flex-end" },
      mb: compact ? 0 : { lg: 0.5 },
    }),
    dataSourcesIndexLabel: { fontWeight: 600, py: 1 },
    dataSourcesGrid: { p: 1.5 },
    sourceDateRangesStack: { mb: 2 },
    sourceDateRangesGrid: { p: 1.5 },
    versionHistoryHeader: { mb: 1 },
    versionHistorySubtitle: { display: "block", mb: 1.5 },
    versionHistoryError: { mb: 1 },
    versionHistoryLoading: { display: "flex", justifyContent: "center", py: 2 },
    versionHistoryRow: { p: 1, borderRadius: 1, border: 1, borderColor: "divider" },
  };
}

export interface DesignSidebarComponentStyles {
  root: AppSx;
  tabs: AppSx;
  tab: AppSx;
  widgetTabContent: AppSx;
  decorTabContent: AppSx;
  queriesTabContent: AppSx;
  hint: AppSx;
}

export function buildDesignSidebarComponentStyles(theme: Theme): DesignSidebarComponentStyles {
  return {
    root: {
      width: { xs: "100%", lg: 450 },
      flexShrink: 0,
      position: { lg: "sticky" },
      top: { lg: 16 },
      alignSelf: { lg: "flex-start" },
      zIndex: { lg: 2 },
      maxHeight: { xs: 360, lg: "calc(100vh - 32px)" },
      display: "flex",
      flexDirection: "column",
      borderRadius: 2,
      border: "1px solid",
      borderColor: "divider",
      bgcolor: "background.paper",
      overflow: "hidden",
      boxShadow:
        theme.palette.mode === "dark"
          ? "none"
          : "0 1px 3px rgba(15, 23, 42, 0.08)",
    },
    tabs: { minHeight: 42, mx: 1.25, mt: 1, mb: 0.5, width: "fit-content", maxWidth: "calc(100% - 20px)" },
    tab: { minHeight: 42, textTransform: "none" },
    widgetTabContent: { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" },
    decorTabContent: { p: 1.5, flex: 1, overflow: "auto" },
    queriesTabContent: { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" },
    hint: { display: "block", mb: 1 },
  };
}

export interface WidgetPaletteComponentStyles {
  paper: AppSx;
  embedded: AppSx;
  header: AppSx;
  headerTitleRow: AppSx;
  headerTitle: AppSx;
  listPadding: AppSx;
  search: AppSx;
  list: AppSx;
  empty: AppSx;
  item: (alreadyPlaced: boolean) => AppSx;
  textBlock: AppSx;
  itemTitle: AppSx;
  placedChip: AppSx;
}

export function buildWidgetPaletteComponentStyles(theme: Theme): WidgetPaletteComponentStyles {
  return {
    paper: {
      width: { xs: "100%", lg: 280 },
      flexShrink: 0,
      maxHeight: { xs: 260, lg: "calc(100vh - 240px)" },
      display: "flex",
      flexDirection: "column",
      borderRadius: 2,
      border: "1px solid",
      borderColor: "divider",
      bgcolor: "background.paper",
      overflow: "hidden",
      boxShadow:
        theme.palette.mode === "dark"
          ? "none"
          : "0 1px 3px rgba(15, 23, 42, 0.08)",
    },
    embedded: {
      display: "flex",
      flexDirection: "column",
      flex: 1,
      minHeight: 0,
      overflow: "hidden",
    },
    header: {
      px: 2,
      py: 1.5,
      borderBottom: "1px solid",
      borderColor: "divider",
      bgcolor: alpha(
        theme.palette.primary.main,
        theme.palette.mode === "dark" ? 0.1 : 0.04
      ),
    },
    headerTitleRow: { mb: 0.5 },
    headerTitle: { flex: 1 },
    listPadding: { p: 1.5, flex: 1, overflow: "auto" },
    search: { mb: 1.5 },
    list: { display: "flex", flexDirection: "column", gap: 0.75 },
    empty: { py: 2, textAlign: "center" },
    item: (alreadyPlaced) => ({
      display: "flex",
      alignItems: "center",
      gap: 0.75,
      px: 1.25,
      py: 1,
      borderRadius: 1.5,
      border: "1px solid",
      borderColor: alreadyPlaced ? "action.disabled" : "divider",
      bgcolor: alreadyPlaced ? "action.hover" : "background.paper",
      opacity: alreadyPlaced ? 0.55 : 1,
      cursor: alreadyPlaced ? "not-allowed" : "grab",
      transition: "border-color 0.15s, background-color 0.15s, box-shadow 0.15s",
      ...(!alreadyPlaced
        ? {
            "&:hover": {
              borderColor: "primary.main",
              bgcolor: alpha(theme.palette.primary.main, 0.04),
            },
          }
        : {}),
    }),
    itemTitle: { fontWeight: 600 },
    textBlock: { flex: 1, minWidth: 0 },
    placedChip: { mt: 0.25, height: 18, fontSize: 10 },
  };
}

export interface DashboardWidgetEditDialogComponentStyles {
  dialogContent: AppSx;
  loadingStack: AppSx;
  formStack: AppSx;
  previewBox: AppSx;
}

export function buildDashboardWidgetEditDialogComponentStyles(
  _theme: Theme
): DashboardWidgetEditDialogComponentStyles {
  return {
    dialogContent: { gap: 0 },
    loadingStack: { py: 4 },
    formStack: { mt: 0.5 },
    previewBox: {
      border: 1,
      borderColor: "divider",
      borderRadius: 1,
      p: 1,
      minHeight: 220,
      height: 280,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    },
  };
}
